import { z } from 'zod';
import type { WorkSourceDto } from './work';

/**
 * Caselle di posta aziendali (di un ufficio o di una funzione, mai intestate a una persona): il sistema le legge a intervalli (IMAP con password, oppure accesso OAuth2 per
 * Microsoft 365 e Google) per capire come lavora l'azienda, come sono stati gestiti i casi passati e quali
 * documenti girano. Si legge soltanto: il sistema non invia, non sposta e non segna come letti i messaggi.
 */
/** Tipo dell'archivio con i messaggi di posta: lo leggono solo i responsabili. */
export const MAIL_ARCHIVE_TYPE = 'messaggio_posta';

export const MAIL_AUTH = ['password', 'oauth_microsoft', 'oauth_google'] as const;
export type MailAuth = (typeof MAIL_AUTH)[number];

/** Server di una casella trovato dall'indirizzo (configurazione pubblicata dal fornitore, MX del dominio). */
export interface MailDiscovery {
  address: string;
  host: string | null;
  port: number;
  secure: boolean;
  /** Accesso OAuth2 richiesto o consigliato dal fornitore (Microsoft 365, Google). */
  provider: 'microsoft' | 'google' | null;
  /** C'è un'applicazione OAuth della piattaforma: l'azienda non deve registrarne una sua. */
  oauth_ready: boolean;
  source: 'autoconfig' | 'mx' | 'nessuna';
  /** Indirizzo di ritorno da registrare nell'applicazione OAuth dell'azienda. */
  oauth_redirect_uri: string;
}

export interface MailboxDto {
  id: string;
  address: string;
  auth: MailAuth;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  interval_minutes: number;
  status: 'in_attesa' | 'attiva' | 'errore' | 'sospesa';
  last_sync_at: string | null;
  last_result: {
    nuovi?: number;
    analizzati?: number;
    allegati_letti?: number;
    storico_completo?: boolean;
    cartelle?: string[];
  } | null;
  last_error: string | null;
  /** Chi ha confermato che è una casella aziendale, e quando. */
  confirmed_at: string | null;
  syncing: boolean;
  /** Lettura nella coda di lavorazione (ADR 0043): conteggio, scarico, lettura e indice, con il tempo stimato. */
  progress: WorkSourceDto | null;
}

export const MailboxInputSchema = z
  .object({
    address: z.string().trim().toLowerCase().email().max(254),
    /** Conferma di chi collega: è una casella dell'azienda (di un ufficio o di una funzione), non di una persona. */
    aziendale: z.literal(true, {
      errorMap: () => ({
        message: 'Si collegano solo caselle aziendali, non quelle delle persone',
      }),
    }),
    auth: z.enum(MAIL_AUTH),
    host: z.string().trim().min(1).max(253),
    port: z.coerce.number().int().min(1).max(65535).default(993),
    secure: z.boolean().default(true),
    username: z.string().trim().min(1).max(254),
    password: z.string().max(500).optional(),
    /** Applicazione OAuth dell'azienda, se la piattaforma non ne ha una per quel fornitore. */
    client_id: z.string().trim().max(300).optional(),
    client_secret: z.string().max(500).optional(),
    interval_minutes: z.coerce.number().int().min(15).max(1440).default(60),
  })
  .refine((v) => v.auth !== 'password' || !!v.password, {
    message: 'Serve la password (o la password per le app)',
    path: ['password'],
  });
export type MailboxInput = z.infer<typeof MailboxInputSchema>;

export const MailboxUpdateSchema = z.object({
  interval_minutes: z.coerce.number().int().min(15).max(1440).optional(),
  status: z.enum(['attiva', 'sospesa']).optional(),
});

export const MailDiscoverSchema = z.object({
  address: z.string().trim().toLowerCase().email().max(254),
});

/** Un allegato di una mail già in archivio che la lettura non aveva tenuto: si riprende dalla casella (ADR 0019). */
export const MailAttachmentFetchSchema = z.object({
  /** Riferimento del messaggio in archivio (rif / riferimento della riga «Messaggio di posta»). */
  messaggio: z.string().trim().min(3).max(300),
  /** Nome dell'allegato com'è nell'attributo «allegati» del messaggio. */
  allegato: z.string().trim().min(1).max(300),
});
export type MailAttachmentFetch = z.infer<typeof MailAttachmentFetchSchema>;
